import { useCallback, useEffect, useMemo, useState } from 'react';
import { Nami, NamiLanguageCodes, NamiLogLevel } from '@namiml/web-sdk';
import ErrorMessage from '../../components/ErrorMessage';
import OptionSelector, { OptionSelectorOption } from '../../components/OptionSelector';
import EditableButton from '../../components/EditableButton';
import { dispatchSdkStateChange } from '../../hooks/useSdkInitialized';
import { NAMI_SDK_STATE_KEY } from '../../utils/const';
import { namiCompressedInitialConfig } from '../../data/intialConfig/nami';
import { nflInitialConfig } from '../../data/intialConfig/nfl';
import Loader from '../../components/Loader';
import {
  loadSDKConfig,
  getHostOptions,
  getPlatformOptions,
  getLogLevelOptions,
  getLanguageOptions,
  getFormFactorOptions,
  getInitialConfigModeOptions,
  getDebugFlags,
  resolveImplies,
  getHostURL,
  getCustomHostDependency,
  getCustomAppPlatformIdDependency,
  type HostOption,
  type PlatformOption,
  type DebugFlag,
} from '../../config/SDKConfigReader';
import { loadInitialConfigFromNetwork } from '../../config/getInitialConfig';

// Friendly labels for the initial-config-mode picker, matching the other SDK test apps.
// "b64_api" is the API-fetch (encoding=b64_gzip) mode.
const INITIAL_CONFIG_MODE_LABELS: Record<string, string> = {
  none: 'None',
  json_file: 'JSON File',
  b64_file: 'B64 File',
  b64_api: 'B64 API',
};

interface ConfigPanelProps {
  onConfigured: () => void;
}

const ConfigPanel: React.FC<ConfigPanelProps> = ({ onConfigured }) => {
  // ---------------------------------------------------------------------------
  // Schema-driven option state (loaded on mount)
  // ---------------------------------------------------------------------------
  const [hostOptions, setHostOptions] = useState<HostOption[]>([]);
  const [platformPresets, setPlatformPresets] = useState<PlatformOption[]>([]);
  const [logLevelValues, setLogLevelValues] = useState<string[]>([]);
  const [languageValues, setLanguageValues] = useState<string[]>([]);
  const [formFactorValues, setFormFactorValues] = useState<string[]>([]);
  const [initialConfigModeValues, setInitialConfigModeValues] = useState<
    string[]
  >([]);
  const [debugFlags, setDebugFlags] = useState<DebugFlag[]>([]);
  const [customHostHosts, setCustomHostHosts] = useState<string[]>([]);
  const [customPlatformHosts, setCustomPlatformHosts] = useState<string[]>([]);

  // ---------------------------------------------------------------------------
  // Form state
  // ---------------------------------------------------------------------------
  const [namiHost, setNamiHost] = useState<string>('');
  const [namiCommandAPIHost, setNamiCommandAPIHost] = useState<string>('');
  const [appPlatformID, setAppPlatformID] = useState<string>('');
  const [initialConfigMode, setInitialConfigMode] = useState<string>('none');
  const [language, setLanguage] = useState<NamiLanguageCodes>('en');
  const [logLevel, setLogLevel] = useState<NamiLogLevel>('debug');
  const [formFactor, setFormFactor] = useState<string>('television');
  const [debugState, setDebugState] = useState<Record<string, boolean>>({});
  const [useSplitPosition, setUseSplitPosition] = useState<boolean>(false);
  const [splitPosition, setSplitPosition] = useState<string | undefined>('50');
  const [message, setMessage] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // ---------------------------------------------------------------------------
  // Load config + schema on mount
  // ---------------------------------------------------------------------------
  useEffect(() => {
    let cancelled = false;

    async function init() {
      try {
        const [config, hosts, logLevels, languages, formFactors, configModes, flags, customHostDep, customPlatformDep] =
          await Promise.all([
            loadSDKConfig(),
            getHostOptions(),
            getLogLevelOptions(),
            getLanguageOptions(),
            getFormFactorOptions(),
            getInitialConfigModeOptions(),
            getDebugFlags('web', undefined),
            getCustomHostDependency(),
            getCustomAppPlatformIdDependency(),
          ]);

        if (cancelled) return;

        // Populate schema-driven options
        setHostOptions(hosts);
        setLogLevelValues(logLevels);
        setLanguageValues(languages);
        setFormFactorValues(formFactors);
        setInitialConfigModeValues(configModes);
        setDebugFlags(flags);
        setCustomHostHosts(customHostDep);
        setCustomPlatformHosts(customPlatformDep);

        // Initialize form from config defaults
        setNamiHost(config.connection.nami_host);
        setAppPlatformID(config.connection.app_platform_id);
        setNamiCommandAPIHost(config.connection.custom_host);
        setLanguage(config.sdk_settings.language_code as NamiLanguageCodes);
        setLogLevel(config.sdk_settings.log_level as NamiLogLevel);
        setFormFactor(config.sdk_settings.form_factor || 'television');
        setInitialConfigMode(config.sdk_settings.initial_config_mode || 'none');

        // Initialize debug flags from config
        const initialDebug: Record<string, boolean> = {};
        for (const flag of flags) {
          initialDebug[flag.key] = config.debug[flag.key] ?? flag.default;
        }
        setDebugState(initialDebug);

        // Split position
        if (config.testing.split_position_override !== null) {
          setUseSplitPosition(true);
          setSplitPosition(String(config.testing.split_position_override));
        }

        // Load platform presets for the default host
        const presets = await getPlatformOptions(config.connection.nami_host);
        if (!cancelled) {
          setPlatformPresets(presets);
        }
      } catch (err) {
        if (!cancelled) {
          console.warn('Failed to load SDK config, using built-in defaults', err);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    init();
    return () => { cancelled = true; };
  }, []);

  // ---------------------------------------------------------------------------
  // Derived option arrays for OptionSelector
  // ---------------------------------------------------------------------------
  const hostSelectorOptions = useMemo<OptionSelectorOption<string>[]>(
    () => hostOptions.map((h) => ({ value: h.value, label: h.label })),
    [hostOptions],
  );

  const languageOptions = useMemo<OptionSelectorOption<string>[]>(
    () => languageValues.map((code) => ({ value: code, label: code.toUpperCase() })),
    [languageValues],
  );

  const logLevelOptions = useMemo<OptionSelectorOption<string>[]>(
    () => logLevelValues.map((level) => ({ value: level, label: level.toUpperCase() })),
    [logLevelValues],
  );

  const formFactorOptions = useMemo<OptionSelectorOption<string>[]>(
    () =>
      formFactorValues.map((ff) => ({
        value: ff,
        label: ff.charAt(0).toUpperCase() + ff.slice(1),
      })),
    [formFactorValues],
  );

  const initialConfigModeOptions = useMemo<OptionSelectorOption<string>[]>(
    () =>
      initialConfigModeValues.map((mode) => ({
        value: mode,
        label: INITIAL_CONFIG_MODE_LABELS[mode] ?? mode,
      })),
    [initialConfigModeValues],
  );

  // Whether to show custom host input
  const showCustomHost = useMemo(
    () => customHostHosts.includes(namiHost),
    [customHostHosts, namiHost],
  );

  // Whether to show custom app platform id input
  const showCustomPlatformId = useMemo(
    () => customPlatformHosts.includes(namiHost),
    [customPlatformHosts, namiHost],
  );

  // Whether current host has platform presets (non-custom hosts)
  const hasPresets = platformPresets.length > 0;

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------
  const setupAPIHost = useCallback(
    async (value: string) => {
      setNamiHost(value);
      setError('');

      // Load host URL for hosts with x-ui-host-urls
      const url = await getHostURL(value);
      if (url && !showCustomHost) {
        setNamiCommandAPIHost(url);
      } else if (!showCustomHost && value !== 'custom') {
        setNamiCommandAPIHost('');
      }

      // Refresh platform presets
      const presets = await getPlatformOptions(value);
      setPlatformPresets(presets);

      if (presets.length === 0 && !customPlatformHosts.includes(value)) {
        // Clear platform ID for hosts without presets (unless it's a custom host)
        setAppPlatformID('');
      }
    },
    [showCustomHost, customPlatformHosts],
  );

  const setupAppPlatformID = useCallback(
    (selected: string) => {
      const next = appPlatformID === selected ? '' : selected;
      setAppPlatformID(next);
    },
    [appPlatformID],
  );

  const handleSplitPositionInput = useCallback((raw: string) => {
    const trimmed = raw.trim();
    if (trimmed === '') {
      setSplitPosition(undefined);
      return;
    }
    const valid = /^\d{1,3}(?:\.\d+)?$/.test(trimmed);
    if (!valid) return;
    const num = Number(trimmed);
    if (Number.isNaN(num)) return;
    const clamped = Math.min(100, Math.max(0, num));
    setSplitPosition(String(clamped));
  }, []);

  const toggleSplitPosition = useCallback(() => {
    setUseSplitPosition((prev) => {
      const next = !prev;
      if (next && (!splitPosition || splitPosition === '')) {
        setSplitPosition('50');
      }
      if (!next) {
        setSplitPosition(undefined);
      }
      return next;
    });
  }, [splitPosition]);

  const toggleDebugFlag = useCallback((key: string) => {
    setDebugState((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const configureSDK = useCallback(async () => {
    setIsLoading(true);
    setMessage('');
    setError('');

    if (!appPlatformID) {
      setError('App Platform ID is required.');
      setIsLoading(false);
      return;
    }

    try {
      // Build namiCommands from schema implies + debug flags
      const commands: string[] = [];

      // Add validate_product_groups if enabled
      if (debugState['validate_product_groups']) {
        commands.push('validateProductGroups');
      }

      // Resolve host implies (e.g. useStagingAPI)
      const impliedCommands = await resolveImplies(namiHost);
      commands.push(...impliedCommands);

      // For custom/localhost hosts, use the explicit command API host URL
      if (showCustomHost || showCustomPlatformId) {
        const hostURL = namiCommandAPIHost.trim();
        if (showCustomHost && !hostURL) {
          setError('Provide a command API host for the selected Nami host.');
          setIsLoading(false);
          return;
        }
        if (hostURL) {
          // Only add if not already implied by the host
          if (!impliedCommands.length) {
            commands.push(`namiAPIHost=${hostURL}`);
          }
        }
      } else {
        // For non-custom hosts that have a URL in the schema (e.g. uspod2)
        const url = await getHostURL(namiHost);
        if (url && !impliedCommands.length) {
          commands.push(`namiAPIHost=${url}`);
        }
      }

      if (debugState['disable_campaign_updates']) {
        commands.push('disableCampaignUpdates');
      }

      if (useSplitPosition && splitPosition !== undefined && splitPosition !== '') {
        const num = Number(splitPosition);
        if (!Number.isNaN(num)) {
          commands.push(`splitPosition=${num}`);
        }
      }

      // Resolve the initial config based on the selected mode:
      //  - none      → no initial config
      //  - b64_api   → fetch over the network (60s timeout + 1 retry on timeout)
      //  - otherwise → bundled config (json_file / b64_file)
      let resolvedInitialConfig: Parameters<
        typeof Nami.configure
      >[0]['initialConfig'];
      if (initialConfigMode === 'b64_api') {
        const fetched = await loadInitialConfigFromNetwork(
          namiHost,
          appPlatformID,
          formFactor,
        );
        if (fetched === null) {
          setError('Failed to fetch initial config from the network.');
          return;
        }
        resolvedInitialConfig = fetched;
      } else if (initialConfigMode !== 'none') {
        resolvedInitialConfig =
          namiHost === 'uspod2' ? nflInitialConfig : namiCompressedInitialConfig;
      }

      const state = await Nami.configure({
        appPlatformID,
        namiLanguageCode: language,
        logLevel,
        maxLogging: debugState['max_logging'] ?? false,
        initialConfig: resolvedInitialConfig,
        namiCommands: commands,
        formFactor,
      });

      localStorage.setItem(NAMI_SDK_STATE_KEY, JSON.stringify(state));
      dispatchSdkStateChange();

      if (state.sdkInitialized) {
        setMessage('SDK configured successfully');
        onConfigured();
      } else {
        setMessage('SDK not configured');
      }
    } catch (err) {
      const details = err instanceof Error ? err.message : String(err);
      setError(`Failed to configure SDK: ${details}`);
    } finally {
      setIsLoading(false);
    }
  }, [
    appPlatformID,
    debugState,
    formFactor,
    language,
    logLevel,
    namiCommandAPIHost,
    namiHost,
    onConfigured,
    initialConfigMode,
    showCustomHost,
    showCustomPlatformId,
    splitPosition,
    useSplitPosition,
  ]);

  const handleClearData = useCallback(async () => {
    try {
      await Nami.reset();
      setMessage('SDK data cleared.');
      setError('');
    } catch (err) {
      const details = err instanceof Error ? err.message : String(err);
      setError(`Clear failed: ${details}`);
    }
  }, []);

  return (
    <div className="panel stack">
      {isLoading && <Loader />}
      <div>
        <h1 className="panel-title">Configure Nami SDK</h1>
        <p className="panel-subtitle">Match the Next.js sample configuration flow with remote-friendly controls.</p>
      </div>

      {message && <ErrorMessage message={message} variant="success" />}
      {error && <ErrorMessage message={error} />}

      <OptionSelector
        title="Nami Host"
        value={namiHost}
        options={hostSelectorOptions}
        onSelect={(value) => setupAPIHost(value as string)}
        layout="column"
      />

      {showCustomHost && (
        <EditableButton
          label="Command API Host"
          value={namiCommandAPIHost}
          placeholder="Set Nami command API host"
          onSubmit={setNamiCommandAPIHost}
        />
      )}

      {hasPresets && (
        <div className="panel">
          <div className="panel-title">App Platform ID Presets</div>
          <div className="panel-subtitle">
            Choose a preset to toggle quickly between environments.
          </div>
          <div className="option-grid" data-layout="column">
            {platformPresets.map((preset) => (
              <button
                key={preset.value}
                type="button"
                className={`tv-button${appPlatformID === preset.value ? ' active' : ''}`}
                onClick={() => setupAppPlatformID(preset.value)}
                onKeyDown={(e) => {
                  if (e.keyCode === 142 || e.keyCode === 195) {
                    e.preventDefault();
                    setupAppPlatformID(preset.value)
                  }
                }}
              >
                {preset.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {showCustomPlatformId && (
        <EditableButton
          label="Custom App Platform ID"
          value={appPlatformID}
          placeholder="Enter custom App Platform ID"
          onSubmit={(value) => {
            setAppPlatformID(value.trim());
          }}
        />
      )}

      {!showCustomPlatformId && (
        <EditableButton
          label="App Platform ID"
          value={appPlatformID}
          placeholder="Enter App Platform ID"
          onSubmit={(value) => {
            setAppPlatformID(value.trim());
          }}
        />
      )}

      <div className="option-grid" data-layout="row">
        {debugFlags.map((flag) => (
          <button
            key={flag.key}
            type="button"
            className={`tv-button${debugState[flag.key] ? ' active' : ''}`}
            onClick={() => toggleDebugFlag(flag.key)}
          >
            {debugState[flag.key] ? `${flag.label}: On` : `${flag.label}: Off`}
          </button>
        ))}
        <button
          type="button"
          className={`tv-button${useSplitPosition ? ' active' : ''}`}
          onClick={toggleSplitPosition}
        >
          {useSplitPosition ? 'Split Position: On' : 'Split Position: Off'}
        </button>
      </div>

      {useSplitPosition && (
        <EditableButton
          label="Split Position (%)"
          value={splitPosition ?? ''}
          placeholder="0 - 100"
          onSubmit={handleSplitPositionInput}
        />
      )}

      <OptionSelector
        title="Language"
        value={language}
        options={languageOptions}
        onSelect={(value) => setLanguage(value as NamiLanguageCodes)}
        layout="row"
        pageSize={languageOptions.length}
      />

      <OptionSelector
        title="Log Level"
        value={logLevel}
        options={logLevelOptions}
        onSelect={(value) => setLogLevel(value as NamiLogLevel)}
      />

      <OptionSelector
        title="Form Factor"
        value={formFactor}
        options={formFactorOptions}
        onSelect={(value) => setFormFactor(value as string)}
      />

      <OptionSelector
        title="Initial Config Mode"
        value={initialConfigMode}
        options={initialConfigModeOptions}
        onSelect={(value) => setInitialConfigMode(value as string)}
      />

      <div className="inline-actions" style={{ justifyContent: 'flex-end', gap: '12px' }}>
        <button 
          type="button" 
          className="tv-button" 
          onClick={configureSDK} 
          disabled={isLoading}
          onKeyDown={(e) => {
            if (e.keyCode === 142 || e.keyCode === 195) {
              e.preventDefault();
              configureSDK()
            }
          }}
        >
          {isLoading ? 'Configuring SDK...' : 'Configure SDK'}
        </button>
        <button
          type="button" 
          className="tv-button" 
          onClick={handleClearData}
          onKeyDown={(e) => {
            if (e.keyCode === 142 || e.keyCode === 195) {
              e.preventDefault();
              handleClearData()
            }
          }}>
          Clear Data
        </button>
      </div>
    </div>
  );
};

export default ConfigPanel;
